import {
  AddressKind,
  AddressModel,
  ContactKind,
  CustomerIndexViewModel,
  PersonModel,
  PersonRole,
} from '@ail/customers-api'
import { VehicleReturnInputModel, VehicleReturnViewModel } from '@ail/orders-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import './ReturnSchedulerModal.scss'
import { usePutVehicleReturnMutation } from '../../../redux/api/ordersApi'
import { useGetCustomerPersonsQuery } from '../../../redux/api/customerApi'
import { addDaysToDate, printDate, zeroTimeZoneDate } from '../../../helpers/Utils'
import { ICalendarInfo } from '../../../types/interfaces'
import { monthNames } from '../../../types/constants'
import LicencePlateFlag from '../../common/LicencePlateFlag/LicencePlateFlag'
import ContactListItem from '../../common/Modals/SchedulerModal/ContactListItem'
import AddressListItem from '../../common/Modals/SchedulerModal/AddressListItem'
import Scheduler from '../../common/Modals/SchedulerModal/Scheduler/Scheduler'
import { Modal } from '../../common/Modals/Modal'
import { ContractViewModel } from '@ail/contracts-api'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'
import { ReturnFormData } from '../SchedulerModals/CombinedScheduler'
export interface ReturnsDataRow {
  contract: ContractViewModel
  customer?: CustomerIndexViewModel
  costCenter: string
  returnData?: VehicleReturnViewModel
}
interface IProps {
  /** Modal is visible */
  visible: boolean
  /** On close button was pressed */
  onClose: () => void
  /** On confirm button was pressed. */
  onConfirm?: (day: number) => void
  /** Data from selected row. */
  dataRow: ReturnsDataRow
  combinedView?: boolean
  autoFillInfo?: ReturnFormData
  canAutoFill?: boolean
  onAutoFillComplete?: () => void
  onUserConfirmation?: (val: boolean) => void
  onManualDataUpdate?: (data: ReturnFormData) => void
  onReadOnlyToggle?: (val: boolean) => void
}

export const ReturnSchedulerModal = (props: IProps) => {
  const { contract, customer, costCenter, returnData } = props.dataRow
  const {
    combinedView,
    autoFillInfo,
    onAutoFillComplete,
    canAutoFill,
    onUserConfirmation,
    onManualDataUpdate,
    onReadOnlyToggle,
  } = props
  const { t } = useTranslation('common')
  const [updateVehicleReturnOrder] = usePutVehicleReturnMutation()
  const persons = useGetCustomerPersonsQuery({
    customerId: customer?.customer?.id ?? '',
    roles: [PersonRole.Primary, PersonRole.FleetManager, PersonRole.ObjectManager],
  })
  //Calendar
  const [readOnly, setReadOnly] = useState(true)
  const [date, setDate] = useState<number>(Date.now())
  const [selectedDay, setSelectedDay] = useState<number | undefined>()
  //Selectors & textarea
  const [selectedContact, setSelectedContact] = useState<PersonModel>()
  const [selectedAddress, setSelectedAddress] = useState<AddressModel>()
  const [manualContact, setManualContact] = useState<string>()

  const [manualAddress, setManualAddress] = useState<string>()
  //Selector Lists togglers
  const [contactOpen, setContactOpen] = useState(false)
  const [addressOpen, setAddressOpen] = useState(false)
  //Search
  const [searchString, setSearchString] = useState('')
  const [searchAddressString, setSearchAddressString] = useState('')
  const [userConfirmation, setUserConfirmation] = useState(false)
  const [dateMissing, setDateMissing] = useState(false)
  const [requiredFieldsMissing, setRequiredFieldsMissing] = useState(false)
  const [noPhoneWarning, setNoPhoneWarning] = useState(false)

  useEffect(() => {
    if (autoFillInfo && canAutoFill && onAutoFillComplete) {
      if (readOnly) {
        setManualAddress(autoFillInfo.scheduledReturnAddress)
        setManualContact(autoFillInfo.scheduledReturnContact)
        onAutoFillComplete()
      }
      if (autoFillInfo.scheduledReturnDate) {
        setSelectedDay(new Date(autoFillInfo.scheduledReturnDate.slice(0, -9)).getTime())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoFill])
  //Auto-fills contact text-area on selection
  useEffect(() => {
    const contact = selectedContact ? getContactDisplayInfo(selectedContact) : ''

    setManualContact(contact)
  }, [selectedContact])

  //Auto-fills address text-area on selection
  useEffect(() => {
    const address = selectedAddress
      ? `${selectedAddress.street}, ${selectedAddress.postalCode}, ${selectedAddress.city}`
      : ''
    setManualAddress(address)
  }, [selectedAddress])

  /** FIltered persons based on relevant costCenter (passed from parent). If no costCenter matches, then uses all persons from response   */
  const relevantPersons = useMemo(() => {
    if (!persons.data) return []
    const foundPersons = persons.data.filter((p) => p.unitName === costCenter)
    return foundPersons.length > 0 ? foundPersons : persons.data
  }, [costCenter, persons.data])
  /** FIltered addresses based on relevant persons and address kinds.*/
  const relevantAddresses = useMemo(() => {
    const validKinds = [AddressKind.Delivery, AddressKind.GroupShippingAddress, AddressKind.Primary]
    const streetSet = new Set()
    return relevantPersons?.flatMap((c) => {
      return c.addresses.filter((a) => {
        if (validKinds.includes(a.kind) && !streetSet.has(a.street)) {
          streetSet.add(a.street)
          return true
        }
        return false
      })
    })
  }, [relevantPersons])

  const filteredPersons = useMemo(() => {
    return relevantPersons
      .filter((p) => p.displayName?.toLowerCase().includes(searchString.toLowerCase()))
      .sort((a, b) => (a.displayName?.toLowerCase() ?? '').localeCompare(b.displayName?.toLowerCase() ?? ''))
  }, [relevantPersons, searchString])

  const filteredAddresses = useMemo(() => {
    return relevantAddresses.filter((a) => {
      const searchString = searchAddressString.toLowerCase()
      return (
        a.street?.toLowerCase().includes(searchString) ||
        a.city?.toLowerCase().includes(searchString) ||
        a.country?.toLowerCase().includes(searchString)
      )
    })
  }, [relevantAddresses, searchAddressString])

  // Automatically opens selector lists when search string exists
  useEffect(() => {
    if (searchString) {
      setContactOpen(true)
    }
  }, [searchString])
  useEffect(() => {
    if (searchAddressString) {
      setAddressOpen(true)
    }
  }, [searchAddressString])

  // #region Data Aggregation

  const scheduledDate = autoFillInfo?.confirmedDeliveryDate
  const earliestDate = useMemo(() => {
    return new Date(returnData?.proposedReturnDate ?? 0)
  }, [returnData?.proposedReturnDate])

  const actualMinDate = useMemo(() => {
    const now = new Date()
    const date =
      combinedView && scheduledDate
        ? new Date(scheduledDate)
        : earliestDate > now
        ? earliestDate
        : addDaysToDate(now, 2)
    setDate(addDaysToDate(date, 1).getTime())
    if (earliestDate > now) {
      setSelectedDay(date.getTime())
    }
    return date
  }, [combinedView, earliestDate, scheduledDate])

  const calendarInfoToShow: ICalendarInfo = useMemo(() => {
    const vehicle = contract?.vehicle
    const logo = vehicle?.makeName?.toLowerCase().includes('ladestation')
      ? 'dummy'
      : vehicle?.makeName?.replace(/\s+/g, '-').toLowerCase()

    const dateToCalcMax = combinedView && scheduledDate ? actualMinDate : earliestDate

    const maxDate = addDaysToDate(dateToCalcMax, 9)
    return {
      selectedDay: actualMinDate.getTime(),
      minDate: actualMinDate.getTime(),
      maxDate: maxDate.getTime(),
      model: `${vehicle?.makeName} ${vehicle?.modelName}`.toUpperCase(),
      brandImage: `../../../../BW/${logo?.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.svg`,
      plateNumber: vehicle?.licensePlate ? vehicle.licensePlate.licenseNumber : undefined,
      driverName: vehicle?.driver?.displayName ?? '',
      companyName: customer?.customer?.name ?? '',
    }
  }, [actualMinDate, combinedView, customer?.customer?.name, earliestDate, scheduledDate, contract])

  // #endregion
  // #region Calendar FX
  const selectDay = (day?: number) => {
    if (day) {
      const dateZeroTimezone = zeroTimeZoneDate(new Date(day)).getTime()
      setDate(dateZeroTimezone)
      setSelectedDay(dateZeroTimezone)
    } else {
      setSelectedDay(undefined)
      setDate(Date.now())
    }
    setDateMissing(false)
  }

  /**
   * Select next month.
   */
  const plusMonth = () => {
    let month = new Date(date).getMonth()
    let year = new Date(date).getFullYear()

    if (month === 11) {
      month = 1
      year++
    } else {
      month++
    }

    setDate(new Date(year, month, 1).getTime())
  }

  /**
   * Select previous month.
   */
  const minusMonth = () => {
    let month = new Date(date).getMonth()
    let year = new Date(date).getFullYear()

    if (month === 0) {
      month = 11
      year--
    } else {
      month--
    }

    setDate(new Date(year, month, 1).getTime())
  }

  const requiredDataIsValid = useCallback(() => {
    if (!manualAddress || !manualContact || (!selectedDay && !combinedView)) {
      setRequiredFieldsMissing(true)
      setDateMissing(true)
      return false
    } else return true
  }, [combinedView, manualAddress, manualContact, selectedDay])

  const formatDateString = (dateNumber?: number | null) => {
    if (!dateNumber) return undefined
    const date = new Date(dateNumber)
    return isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, -5)
  }

  const confirm = useCallback(
    (confirmed: boolean) => {
      if (!requiredDataIsValid()) return
      if (!returnData && !combinedView) return
      if (!confirmed) return

      const model: VehicleReturnInputModel = {
        scheduledReturnDate: formatDateString(selectedDay),
        scheduledReturnAddress: manualAddress,
        scheduledReturnContact: manualContact,
      }
      const modelForCombined: ReturnFormData = { ...model, confirmedDeliveryDate: scheduledDate }
      if (combinedView && onManualDataUpdate) {
        onManualDataUpdate(modelForCombined)
        return
      }
      if (returnData) updateVehicleReturnOrder({ orderId: returnData.id, model })
      props.onClose()
    },
    [
      combinedView,
      manualAddress,
      manualContact,
      onManualDataUpdate,
      props,
      requiredDataIsValid,
      returnData,
      selectedDay,
      updateVehicleReturnOrder,
      scheduledDate,
    ]
  )
  /** Autofilling address selection if any of the validKinds are present in address array of person. 
 Does not clear address value, when contact is reset*/
  function autoFillAddress(person: PersonModel) {
    const validKinds = [AddressKind.Delivery, AddressKind.GroupShippingAddress, AddressKind.Primary]
    const validAddress = person.addresses.find((a) => {
      if (validKinds.includes(a.kind)) return a
    })
    setSelectedAddress(validAddress)
  }

  /** Displays basic info about person as a string. Contact info can be adjusted based on ContactKind */
  function getContactDisplayInfo(person: PersonModel) {
    const phone = person.contacts.find((c) =>
      [ContactKind.PhoneNumber, ContactKind.MobileNumber].includes(c.kind)
    )?.value
    const email = person.contacts.find((c) => c.kind === ContactKind.Email)?.value
    return [person.displayName, phone, email].filter(Boolean).join(', ')
  }
  // #endregion

  // #region CombinedView
  function handleReadOnlyToggle() {
    setReadOnly(!readOnly)
    if (onReadOnlyToggle) onReadOnlyToggle(!readOnly)
  }

  const handleConfirmationChange = useCallback(() => {
    if (requiredDataIsValid()) {
      const newConfirmed = !userConfirmation
      setUserConfirmation(newConfirmed)

      if (onUserConfirmation) {
        onUserConfirmation(newConfirmed)
      }
      if (combinedView) confirm(newConfirmed)
    }
  }, [requiredDataIsValid, userConfirmation, onUserConfirmation, confirm, combinedView])
  // #endregion
  useEffect(() => {
    if (returnData) {
      setManualAddress(returnData.scheduledReturnAddress)
      setManualContact(returnData.scheduledReturnContact)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  //* Resets userConfirmation when confirmedDeliveryDate from Delivery calendar changed
  useEffect(() => {
    if (autoFillInfo?.confirmedDeliveryDate && readOnly) {
      setUserConfirmation(false)
    }
  }, [autoFillInfo?.confirmedDeliveryDate, readOnly])
  // #region UI
  const confirmContainer = (
    <div className="scheduler-confirm-area">
      <div className="transparent-button negative" onClick={props.onClose}>
        <i className="icon-close"></i>
        {t('CLOSE')}
      </div>
      <div
        className={`transparent-button ${userConfirmation && !combinedView ? 'positive' : 'disabled'}`}
        onClick={userConfirmation && !combinedView ? () => confirm(true) : undefined}
      >
        <i className="icon-check"></i>
        {t('CONFIRM')}
      </div>
    </div>
  )

  const shedulerContent = (
    <div className="sheduler-content return">
      <div>
        <div className="head">
          <div className="title">
            <i className={combinedView ? 'icon-garageIn' : 'icon-calendar'}></i>
            <span>{t('RETURN_INFO')}</span>
          </div>
          {combinedView && (
            <div className="checkBoxWrapper">
              <label>
                <i className="icon-export" /> {t('RETURN_ORDER_SAME_ADDRESS')}
              </label>
              <input type="checkbox" checked={readOnly} onChange={handleReadOnlyToggle} />
            </div>
          )}
        </div>
        <div className="info">
          <div className="first">
            <div className="flex">
              <div>
                <i className="icon-customers"></i>
                {calendarInfoToShow.companyName}
              </div>
              <div>
                <i className="icon-driver"></i>
                {calendarInfoToShow?.driverName}
              </div>
            </div>
            <div>
              <img width="20" src={calendarInfoToShow.brandImage} />
              {calendarInfoToShow.model}
            </div>
          </div>
          <div className="second">
            <div className="user-info">
              {calendarInfoToShow?.plateNumber && (
                <div>
                  {calendarInfoToShow?.plateNumber && (
                    <LicencePlateFlag licenceNumber={calendarInfoToShow?.plateNumber} width="12" height="14" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="calendarAddress">
          <div className="inputGroup">
            <div className="flex">
              <div>
                <i className="icon-manager" />
                <label>{t('RETURN_CONTACT')}</label>
                {noPhoneWarning && (
                  <i className="icon-warning">
                    <span className="tooltip">{t('MISSING_CONTACT_PHONE')}</span>
                  </i>
                )}
              </div>
            </div>
            <div className="contracts-dropdown-filter-ui">
              <div className="search">
                <input
                  disabled={combinedView && readOnly}
                  type="text"
                  list="autocompleteOff"
                  placeholder={t('SEARCH_OR_SELECT')}
                  value={selectedContact ? getContactDisplayInfo(selectedContact) : searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  onClick={() => {
                    setAddressOpen(false)
                    setContactOpen(!contactOpen)
                  }}
                />
                {selectedContact && (
                  <i
                    className="icon-clear"
                    style={{ marginRight: '15px' }}
                    onClick={() => {
                      setContactOpen(false)
                      setSelectedContact(undefined)
                      setSearchString('')
                    }}
                  ></i>
                )}
                {!selectedContact && persons.isLoading && <LoadingSpinner />}
                <i
                  className={`${contactOpen ? 'icon-up' : 'icon-bottom'}`}
                  onClick={() => {
                    if (!combinedView || !readOnly) {
                      setAddressOpen(false)
                      setContactOpen(!contactOpen)
                    }
                  }}
                />
              </div>
              {contactOpen && !!filteredPersons?.length && (
                <div className="search-results">
                  <ul>
                    {filteredPersons?.map((m) => (
                      <ContactListItem
                        person={m}
                        onClick={(val) => {
                          setContactOpen(false)
                          setSelectedContact(m)
                          autoFillAddress(m)
                          setNoPhoneWarning(val)
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <textarea
              disabled={combinedView && readOnly}
              className={`result ${requiredFieldsMissing && !manualContact ? `requiredField` : ''}`}
              placeholder={requiredFieldsMissing && !manualContact ? t('MISSING_DELIVERY_CONTACT') : ''}
              value={manualContact ? manualContact : ''}
              onChange={(e) => setManualContact(e.target.value)}
            />
          </div>
          <div className="inputGroup">
            <div className="flex">
              <div>
                <i className="icon-marker" />
                <label>{t('RETURN_ADDRESS')}</label>
              </div>
            </div>
            <div className="contracts-dropdown-filter-ui">
              <div className="search">
                <input
                  disabled={combinedView && readOnly}
                  type="text"
                  list="autocompleteOff"
                  placeholder={t('SEARCH_OR_SELECT')}
                  value={
                    selectedAddress
                      ? `${selectedAddress.street}, ${selectedAddress.postalCode}, ${selectedAddress.city}`
                      : searchAddressString
                  }
                  onChange={(e) => setSearchAddressString(e.target.value)}
                  onClick={() => {
                    setAddressOpen(!addressOpen)
                    setContactOpen(false)
                  }}
                />
                {selectedAddress && (
                  <i
                    className="icon-clear"
                    style={{ marginRight: '15px' }}
                    onClick={() => {
                      setAddressOpen(false)
                      setSelectedAddress(undefined)
                      setSearchAddressString('')
                    }}
                  ></i>
                )}
                <i
                  className={`${addressOpen ? 'icon-up' : 'icon-bottom'}`}
                  onClick={() => {
                    if (!combinedView || !readOnly) {
                      setAddressOpen(!addressOpen)
                      setContactOpen(false)
                    }
                  }}
                />
              </div>
              {addressOpen && !!relevantAddresses?.length && (
                <div className="search-results">
                  <ul>
                    {filteredAddresses.map((address) => (
                      <AddressListItem
                        address={address}
                        onClick={() => {
                          setAddressOpen(false)
                          setSelectedAddress(address)
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <textarea
              disabled={combinedView && readOnly}
              className={`result ${requiredFieldsMissing && !manualAddress ? `requiredField` : ''}`}
              placeholder={requiredFieldsMissing && !manualAddress ? t('MISSING_DELIVERY_ADDRESS') : ''}
              value={manualAddress ? manualAddress : ''}
              onChange={(e) => setManualAddress(e.target.value)}
            />
          </div>
        </div>
      </div>
      {
        <div className="reminderGroup">
          <p>{t('RETURN_ORDER_REMINDER')}</p>
          <div className="flex">
            <div className="reminderItem">
              <i className="icon-key" />
              <span>{t('REMINDER_KEYS')}</span>
            </div>
            <div className="reminderItem">
              <i className="icon-rollo" />
              <span>{t('REMINDER_BLINDS')}</span>
            </div>
            <div className="reminderItem">
              <i className="icon-doc-registration" />
              <span>{t('REMINDER_REGISTRATION_CARD')}</span>
            </div>
            <div className="reminderItem">
              <i className="icon-tire-storage" />
              <span>{t('REMINDER_TIRES')}</span>
            </div>
          </div>
          {/*<div className="infoText">
              <i className="icon-info" />
              <label>{t('RETURN_ORDER_DATE_WARNING')}</label>
            </div>*/}
        </div>
      }
      <div>
        {
          <div className={`filterBar ${dateMissing && !selectedDay ? `red` : ''}`}>
            {(returnData?.proposedReturnDate || scheduledDate) && (
              <div style={{ fontSize: '13px' }}>
                <i className="icon-info" />
                {`${t('RETURN_PROPOSED')}`}:{' '}
                <strong style={{ fontSize: '15px' }}>{`${printDate(
                  returnData?.proposedReturnDate ?? scheduledDate
                )}`}</strong>
              </div>
            )}
            {dateMissing && !selectedDay && (
              <div className="missingDate">
                <i className="icon-warning" /> {t('MISSING_DATE')}
              </div>
            )}
            <div className="select">
              <i className="icon-left" onClick={minusMonth}></i>
              <span>
                {t(monthNames[new Date(date).getMonth()])} {new Date(date).getFullYear()}
              </span>
              <i className="icon-right" onClick={plusMonth}></i>
            </div>
          </div>
        }
        {
          <Scheduler
            date={date}
            onDateSelected={(day) => selectDay(day)}
            selectedDate={calendarInfoToShow.selectedDay}
            minDate={calendarInfoToShow.minDate}
            maxDate={calendarInfoToShow.maxDate}
            combinedViewAutoUpdate={canAutoFill}
            disableSelection={userConfirmation}
          />
        }
        <div className="checkPro">
          <input type="checkbox" checked={userConfirmation} onChange={handleConfirmationChange} />
          <label style={!userConfirmation ? { color: 'red' } : undefined}>
            <Trans>{t('RETURN_ORDER_CHECK_CONFIRMATION')}</Trans>
          </label>
        </div>
      </div>
    </div>
  )

  return combinedView ? (
    shedulerContent
  ) : (
    <Modal visible={props.visible} onClose={props.onClose} children={shedulerContent} footer={confirmContainer} />
  )
  // #endregion
}
